import { useEffect } from 'react';
import { getUrlQueryParams, setDefaultQueryParamsForView, setUrlQueryParam, getDefaultChartSettings, isSelectionInvalid } from '../actions';

const SUPPORTED_MEASURES = ['PRI', 'SROL', 'FEO', 'ROLJUS', 'JudSys'];
const FALLBACK_COUNTRIES = ['DZ', 'AO', 'BJ', 'BW', 'BF', 'BI', 'CV', 'CM', 'CF', 'TD', 'KM', 'CG', 'CI', 'CD', 'DJ', 'EG', 'GQ', 'ER', 'SZ', 'ET', 'GA', 'GM', 'GH', 'GN', 'GW', 'KE', 'LS', 'LR', 'LY', 'MG', 'MW', 'ML', 'MR', 'MU', 'MA', 'MZ', 'NA', 'NE', 'NG', 'RW', 'ST', 'SN', 'SC', 'SL', 'SO', 'ZA', 'SS', 'SD', 'TG', 'TN', 'UG', 'TZ', 'ZM', 'ZW'];
const RANDOM_VIEWS = ['overview', 'graph', 'table', 'map'];
const OVERVIEW_VARIANTS = [{ subview: 'absoluteTrends' }, { subview: 'annualAvgTrend' }];
const GRAPH_VARIANTS = [
  { subview: 'score', option: 'line' },
  { subview: 'score', option: 'bar' },
  { subview: 'absoluteTrend', option: 'bar' },
  { subview: 'annualAvgTrend', option: 'scatter' },
  { subview: 'correlation', option: 'line', measureCount: 2 },
  { subview: 'correlation', option: 'scatter', measureCount: 2 },
];
const TABLE_VARIANTS = [{ subview: 'score' }, { subview: 'changes' }];
const MAP_VARIANTS = [{ subview: 'score' }];

const VIEW_VARIANTS = {
  overview: OVERVIEW_VARIANTS,
  graph: GRAPH_VARIANTS,
  table: TABLE_VARIANTS,
  map: MAP_VARIANTS,
};

const DEFAULT_MAX_MEASURES = 4;
const DEFAULT_MAX_LOCATIONS = 6;
const OVERVIEW_MAX_MEASURES = 12;
const OVERVIEW_MAX_LOCATIONS = 8;

const needsDefaults = new Set(['graph', 'table', 'map']);

const isTypingContext = (element) => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }
  const tagName = element.tagName;
  return element.isContentEditable || tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA';
};

const pickRandom = (items) => items[Math.floor(Math.random() * items.length)];

const uniqueSelection = (items, count) => {
  if (!items.length || count <= 0) {
    return [];
  }
  const effectiveCount = Math.min(count, items.length);
  const pool = [...items];
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = pool[i];
    pool[i] = pool[j];
    pool[j] = temp;
  }
  return pool.slice(0, effectiveCount);
};

const randomIntBetween = (min, max) => {
  if (max < min) {
    return null;
  }
  if (min === max) {
    return min;
  }
  return min + Math.floor(Math.random() * (max - min + 1));
};

const getRandomCount = (available, rules, defaultMax) => {
  if (typeof available !== 'number') {
    return null;
  }
  const safeDefaultMax = typeof defaultMax === 'number' ? defaultMax : available;
  if (!rules) {
    const max = Math.min(safeDefaultMax, available);
    return Math.max(Math.min(max, 1), 0);
  }
  if (typeof rules.exact === 'number') {
    return available >= rules.exact ? rules.exact : null;
  }
  const min = Math.max(typeof rules.min === 'number' ? rules.min : 1, 0);
  const max = Math.min(typeof rules.max === 'number' ? rules.max : safeDefaultMax, available);
  if (max < min) {
    return null;
  }
  return randomIntBetween(min, max);
};

const countMatchesRules = (count, rules, defaultMax) => {
  if (typeof count !== 'number') {
    return false;
  }
  if (!rules) {
    return count > 0;
  }
  if (typeof rules.exact === 'number') {
    return count === rules.exact;
  }
  const min = Math.max(typeof rules.min === 'number' ? rules.min : 1, 0);
  const maxCandidate = typeof rules.max === 'number' ? rules.max : defaultMax;
  const max = typeof maxCandidate === 'number' ? maxCandidate : defaultMax;
  return count >= min && count <= max;
};

const mergeRules = (baseRules, overrideRules) => {
  if (!overrideRules) {
    return baseRules;
  }
  if (typeof overrideRules.exact === 'number') {
    return { exact: overrideRules.exact };
  }
  return { ...baseRules, ...overrideRules };
};

const isCountFeasible = (available, rules, defaultMax) => {
  if (typeof available !== 'number') {
    return false;
  }
  if (!rules) {
    return available > 0;
  }
  if (typeof rules.exact === 'number') {
    return available >= rules.exact;
  }
  const min = Math.max(typeof rules.min === 'number' ? rules.min : 1, 0);
  const maxCandidate = typeof rules.max === 'number' ? rules.max : defaultMax;
  const max = typeof maxCandidate === 'number' ? Math.min(maxCandidate, available) : available;
  return available >= min && max >= min;
};

const isStrategyFeasible = (strategy, availableMeasures, availableLocations) => isCountFeasible(availableMeasures, strategy.measure, DEFAULT_MAX_MEASURES) && isCountFeasible(availableLocations, strategy.location, DEFAULT_MAX_LOCATIONS);

const fallbackStrategy = {
  measure: { min: 1, max: 1 },
  location: { min: 1, max: 1 },
};

const buildStrategiesForView = (view, variant) => {
  if (view === 'overview') {
    return [
      { measure: { min: 2, max: OVERVIEW_MAX_MEASURES }, location: { min: 1, max: 1 } },
      { measure: { min: 1, max: 1 }, location: { min: 2, max: OVERVIEW_MAX_LOCATIONS } },
    ];
  }

  if (view === 'map') {
    return [{ measure: { min: 1, max: 1 }, location: { min: 2, max: DEFAULT_MAX_LOCATIONS } }];
  }

  if (view === 'graph' && variant?.subview === 'correlation') {
    const measureRequirement = typeof variant?.measureCount === 'number' ? variant.measureCount : 2;
    return [{ measure: { exact: measureRequirement }, location: { min: 0, max: 1 } }];
  }

  if (view === 'table') {
    return [
      { measure: { min: 1, max: 1 }, location: { min: 2, max: DEFAULT_MAX_LOCATIONS } },
      { measure: { min: 2, max: DEFAULT_MAX_MEASURES }, location: { min: 1, max: 1 } },
    ];
  }

  return [
    { measure: { min: 2, max: DEFAULT_MAX_MEASURES }, location: { min: 1, max: 1 } },
    { measure: { min: 1, max: 1 }, location: { min: 2, max: DEFAULT_MAX_LOCATIONS } },
  ];
};

const createVariantOverrides = (variant) => {
  if (!variant) {
    return {};
  }
  const overrides = {};
  if (typeof variant.measureCount === 'number') {
    overrides.measure = { exact: variant.measureCount };
  }
  if (typeof variant.locationCount === 'number') {
    overrides.location = { exact: variant.locationCount };
  }
  return overrides;
};

const buildSelectionStrategies = (view, variant, availableMeasuresCount, availableLocationsCount) => {
  const baseStrategies = buildStrategiesForView(view, variant);
  const overrides = createVariantOverrides(variant);
  const strategiesWithOverrides = baseStrategies
    .map((strategy) => ({
      measure: mergeRules(strategy.measure, overrides.measure),
      location: mergeRules(strategy.location, overrides.location),
    }))
    .filter((strategy) => isStrategyFeasible(strategy, availableMeasuresCount, availableLocationsCount));

  if (strategiesWithOverrides.length) {
    return strategiesWithOverrides;
  }

  const fallbackWithOverrides = {
    measure: mergeRules(fallbackStrategy.measure, overrides.measure),
    location: mergeRules(fallbackStrategy.location, overrides.location),
  };

  if (isStrategyFeasible(fallbackWithOverrides, availableMeasuresCount, availableLocationsCount)) {
    return [fallbackWithOverrides];
  }

  return [];
};

const pickCountsForStrategy = (strategy, availableMeasures, availableLocations) => {
  const measureCount = getRandomCount(availableMeasures, strategy.measure, DEFAULT_MAX_MEASURES);
  if (measureCount === null) {
    return null;
  }
  const locationCount = getRandomCount(availableLocations, strategy.location, DEFAULT_MAX_LOCATIONS);
  if (locationCount === null) {
    return null;
  }
  return { measureCount, locationCount };
};

const resolveCountForShuffle = (currentCount, strategyRule, defaultMax, available) => {
  if (typeof strategyRule?.exact === 'number') {
    return available >= strategyRule.exact ? strategyRule.exact : null;
  }
  const min = Math.max(typeof strategyRule?.min === 'number' ? strategyRule.min : 1, 0);
  const maxCandidate = typeof strategyRule?.max === 'number' ? strategyRule.max : defaultMax;
  const max = Math.min(typeof maxCandidate === 'number' ? maxCandidate : defaultMax, available);
  if (max < min) {
    return null;
  }
  if (typeof currentCount === 'number' && currentCount >= min && currentCount <= max) {
    return Math.min(currentCount, available);
  }
  return randomIntBetween(min, max);
};

const findVariantForView = (view, urlParams) => {
  const variants = VIEW_VARIANTS[view];
  if (!variants || !variants.length) {
    return null;
  }
  return (
    variants.find((variant) => {
      if (variant.subview && variant.subview !== urlParams.subview) {
        return false;
      }
      if (typeof variant.option !== 'undefined' && variant.option !== urlParams.option) {
        return false;
      }
      return true;
    }) || null
  );
};

const parseSelectionParam = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return [];
  }
  return value.split('-').filter(Boolean);
};

const selectionsMatch = (a, b) => {
  if (!a || !b) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

const applyDefaultSettings = (view, updateParam) => {
  if (!needsDefaults.has(view)) {
    return;
  }
  const params = getUrlQueryParams();
  const defaults = getDefaultChartSettings(params);
  if (!defaults || !defaults.length) {
    return;
  }
  defaults.forEach((setting) => {
    const [name, value] = Object.entries(setting)[0];
    updateParam(name, `${value}`);
  });
};

const normaliseEventKey = (event) => {
  if (event.code && /^Key[A-Z]$/.test(event.code)) {
    return event.code.slice(3).toLowerCase();
  }
  const key = event.key;
  if (!key) {
    return '';
  }
  if (key.length === 1) {
    return key.toLowerCase();
  }
  return key.toLowerCase();
};

const applyDefaultParamsForView = (view, dispatch) => {
  const payload = setDefaultQueryParamsForView(view);
  if (dispatch) {
    dispatch({
      type: 'setDefaultQueryParamsForView',
      payload,
    });
  }
};

const createUpdateParam = (dispatch) => (name, value) => {
  const payload = setUrlQueryParam({ name, value });
  if (dispatch) {
    dispatch({
      type: 'setUrlQueryParam',
      payload,
    });
  }
};

export default function useKeyboardShortcuts(state, dispatch) {
  const measures = state?.fullListOfMeasures || [];
  const countries = state?.fullListOfCountries || [];

  useEffect(() => {
    const updateParam = createUpdateParam(dispatch);

    const supportedMeasureCodes = measures.filter((measure) => measure?.s_id && SUPPORTED_MEASURES.includes(measure.s_id)).map((measure) => measure.s_id);
    const fallbackMeasureCodes = supportedMeasureCodes.length > 0 ? supportedMeasureCodes : measures.map((measure) => measure?.s_id).filter(Boolean);
    const availableMeasureCodes = fallbackMeasureCodes.length ? fallbackMeasureCodes : SUPPORTED_MEASURES;

    const availableCountryCodes = countries.map((country) => country?.iso).filter(Boolean);
    const countryPool = availableCountryCodes.length ? availableCountryCodes : FALLBACK_COUNTRIES;

    const selectMeasures = (count = 1) => uniqueSelection(availableMeasureCodes, count);
    const selectLocations = (count = 1) => uniqueSelection(countryPool, count);

    const randomiseView = (targetView) => {
      const availableMeasures = availableMeasureCodes;
      const availableLocations = countryPool;
      if (!availableMeasures.length || !availableLocations.length) {
        return false;
      }

      const view = targetView || pickRandom(RANDOM_VIEWS);
      const variantsForView = (VIEW_VARIANTS[view] && VIEW_VARIANTS[view].length ? VIEW_VARIANTS[view] : [{}]).map((variant) => variant || {});
      let variantOptions = variantsForView
        .map((variant) => ({
          variant,
          strategies: buildSelectionStrategies(view, variant, availableMeasures.length, availableLocations.length),
        }))
        .filter((entry) => entry.strategies.length);

      if (!variantOptions.length) {
        const fallbackStrategies = buildSelectionStrategies(view, null, availableMeasures.length, availableLocations.length);
        if (!fallbackStrategies.length) {
          return false;
        }
        variantOptions = [{ variant: {}, strategies: fallbackStrategies }];
      }

      const attempts = Math.max(variantOptions.length * 3, 6);
      for (let i = 0; i < attempts; i += 1) {
        const { variant, strategies } = pickRandom(variantOptions);
        const strategy = pickRandom(strategies);
        const counts = pickCountsForStrategy(strategy, availableMeasures.length, availableLocations.length);

        if (!counts) {
          continue;
        }

        const { measureCount, locationCount } = counts;
        const selectedMeasures = selectMeasures(measureCount);
        const selectedLocations = selectLocations(locationCount);

        if (!selectedMeasures.length) {
          continue;
        }
        if (locationCount > 0 && !selectedLocations.length) {
          continue;
        }

        updateParam('view', view);
        applyDefaultParamsForView(view, dispatch);

        if (variant.subview) {
          updateParam('subview', variant.subview);
        }

        if (typeof variant.option !== 'undefined') {
          updateParam('option', variant.option);
        }

        applyDefaultSettings(view, updateParam);

        updateParam('meas', selectedMeasures.join('-'));
        if (locationCount > 0) {
          updateParam('loc', selectedLocations.join('-'));
        } else {
          updateParam('loc', '');
        }

        const finalParams = getUrlQueryParams();
        const errors = isSelectionInvalid(state, finalParams);

        if (!errors.length) {
          return true;
        }
      }
      return false;
    };

    const shuffleMeasuresOnly = () => {
      const urlParams = getUrlQueryParams();
      const view = urlParams.view;
      if (!view) {
        return false;
      }

      const availableMeasures = availableMeasureCodes;
      const availableLocations = countryPool;
      const currentMeasures = parseSelectionParam(urlParams.meas);
      const currentLocations = parseSelectionParam(urlParams.loc);
      const variant = findVariantForView(view, urlParams);

      let strategies = buildSelectionStrategies(view, variant, availableMeasures.length, availableLocations.length);
      strategies = strategies.filter((strategy) => countMatchesRules(currentLocations.length, strategy.location, DEFAULT_MAX_LOCATIONS));

      if (!strategies.length) {
        return false;
      }

      const attempts = Math.max(strategies.length * 3, 6);
      for (let i = 0; i < attempts; i += 1) {
        const strategy = pickRandom(strategies);
        const measureCount = resolveCountForShuffle(currentMeasures.length, strategy.measure, DEFAULT_MAX_MEASURES, availableMeasures.length);
        if (measureCount === null || measureCount <= 0) {
          continue;
        }

        const nextMeasures = selectMeasures(measureCount);
        if (!nextMeasures.length) {
          continue;
        }

        if (availableMeasures.length > measureCount && selectionsMatch(nextMeasures, currentMeasures)) {
          continue;
        }

        updateParam('meas', nextMeasures.join('-'));

        const finalParams = getUrlQueryParams();
        if (!isSelectionInvalid(state, finalParams).length) {
          return true;
        }
      }
      return false;
    };

    const shuffleLocationsOnly = () => {
      const urlParams = getUrlQueryParams();
      const view = urlParams.view;
      if (!view) {
        return false;
      }

      const availableMeasures = availableMeasureCodes;
      const availableLocations = countryPool;
      const currentMeasures = parseSelectionParam(urlParams.meas);
      const currentLocations = parseSelectionParam(urlParams.loc);
      const variant = findVariantForView(view, urlParams);

      let strategies = buildSelectionStrategies(view, variant, availableMeasures.length, availableLocations.length);
      strategies = strategies.filter((strategy) => countMatchesRules(currentMeasures.length, strategy.measure, DEFAULT_MAX_MEASURES));

      if (!strategies.length) {
        return false;
      }

      const attempts = Math.max(strategies.length * 3, 6);
      for (let i = 0; i < attempts; i += 1) {
        const strategy = pickRandom(strategies);
        const locationCount = resolveCountForShuffle(currentLocations.length, strategy.location, DEFAULT_MAX_LOCATIONS, availableLocations.length);
        if (locationCount === null) {
          continue;
        }

        const nextLocations = selectLocations(locationCount);

        if (locationCount > 0 && !nextLocations.length) {
          continue;
        }

        if (locationCount > 0 && availableLocations.length > locationCount && selectionsMatch(nextLocations, currentLocations)) {
          continue;
        }

        if (locationCount > 0) {
          updateParam('loc', nextLocations.join('-'));
        } else {
          updateParam('loc', '');
        }

        const finalParams = getUrlQueryParams();
        if (!isSelectionInvalid(state, finalParams).length) {
          return true;
        }
      }
      return false;
    };

    const handleKeyDown = (event) => {
      if (event.repeat || isTypingContext(event.target)) {
        return;
      }

      const usesCommand = event.altKey && (event.ctrlKey || event.metaKey);
      if (!usesCommand) {
        return;
      }

      const key = normaliseEventKey(event);
      if (!key) {
        return;
      }

      let success = false;
      switch (key) {
        case 'r':
          success = randomiseView();
          break;
        case 'o':
          success = randomiseView('overview');
          break;
        case 'g':
          success = randomiseView('graph');
          break;
        case 't':
          success = randomiseView('table');
          break;
        case 'm':
          success = randomiseView('map');
          break;
        case 's':
          success = shuffleMeasuresOnly();
          break;
        case 'l':
          success = shuffleLocationsOnly();
          break;
        default:
          break;
      }

      if (success) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [dispatch, measures, countries, state]);
}
